import { hasAcquisitionCompsheetByTicker } from '../constants/acquisitionCompsheets';
import { isFreeSecurityAnalysisTicker } from '../constants/api';
import { getDevFlag } from '../constants/devFlags';
import { hasSiPartnerHiringByTicker } from '../constants/partnerHiring';
import { getCompanyInfo } from './companyHelpers';

export const COMPANY_VIEW_SCOPES = {
  FINANCIALS: 'financials',
  COMPS: 'comps',
  JOBS: 'jobs',
  DEVELOPER: 'developer',
  GUIDANCE: 'guidance',
  FILINGS: 'filings',
  PARTNERS: 'partners',
  PRIVATE: 'private',
  ACQUISITION: 'acquisition',
  SNAPSHOT_COMPS: 'snapshotComps',
  OPERATING_KPIS: 'operatingKpis',
  HIRING: 'hiring',
  GUIDANCE_COMPANY: 'guidanceCompany',
  SI_HIRING: 'siHiring',
};

const normalizeTicker = (ticker) => String(ticker || '').trim().toUpperCase();

export const getCompanyAvailability = (ticker) => {
  const normalizedTicker = normalizeTicker(ticker);
  const info = getCompanyInfo(normalizedTicker);
  const exists = Boolean(info.raw);
  const isAcquired = info.privateCompanyType === 'acquired';
  const hasPublicAcquisitionHistory = isAcquired && !normalizedTicker.startsWith('PVT-');
  // Software units have no issuer financials; those belong to the parent.
  const isSoftwareUnit = Boolean(info.isSoftwareUnit);
  const showStandardFinancials = exists && !isSoftwareUnit && !info.hasCapTable && (!info.isPrivate || hasPublicAcquisitionHistory);
  const showComps = exists && !isSoftwareUnit && !info.isPrivate && !info.isAcquiredSubsidiary;
  const showPrivateCompanyDetail = exists && info.isPrivate && !hasPublicAcquisitionHistory;
  const showHiringTrends = exists && info.hasJobBoard && !info.isAcquiredSubsidiary;
  const showGuidance = (
    showStandardFinancials
    && Boolean(info.hasGuidance)
    && getDevFlag('GUIDANCE_PREVIEW')
  );
  const showFilingIntelligence = (
    exists
    && Boolean(info.hasFilingIntelligence)
    && getDevFlag('FILING_INTELLIGENCE_PREVIEW')
  );
  const hasPartnerHiring = hasSiPartnerHiringByTicker(normalizedTicker);
  const hasAcquisitionSnapshotComps = hasAcquisitionCompsheetByTicker(normalizedTicker);

  return {
    ticker: normalizedTicker,
    exists,
    info,
    hasFreeSecurityAnalysisAccess: isFreeSecurityAnalysisTicker(normalizedTicker),
    hasPublicAcquisitionHistory,
    showStandardFinancials,
    showComps,
    showPrivateCompanyDetail,
    showHiringTrends,
    showGuidance,
    showFilingIntelligence,
    hasPartnerHiring,
    hasAcquisitionSnapshotComps,
    isAcquiredSubsidiary: Boolean(info.isAcquiredSubsidiary),
    isSoftwareUnit,
    scopes: {
      [COMPANY_VIEW_SCOPES.FINANCIALS]: showStandardFinancials,
      [COMPANY_VIEW_SCOPES.COMPS]: showComps,
      [COMPANY_VIEW_SCOPES.JOBS]: showHiringTrends,
      [COMPANY_VIEW_SCOPES.DEVELOPER]: exists && Boolean(info.hasDevBoard),
      [COMPANY_VIEW_SCOPES.GUIDANCE]: showGuidance,
      [COMPANY_VIEW_SCOPES.FILINGS]: showFilingIntelligence,
      [COMPANY_VIEW_SCOPES.PARTNERS]: hasPartnerHiring,
      [COMPANY_VIEW_SCOPES.PRIVATE]: showPrivateCompanyDetail,
      [COMPANY_VIEW_SCOPES.ACQUISITION]: hasPublicAcquisitionHistory,
      [COMPANY_VIEW_SCOPES.SNAPSHOT_COMPS]: hasPublicAcquisitionHistory && hasAcquisitionSnapshotComps,
      [COMPANY_VIEW_SCOPES.OPERATING_KPIS]: showStandardFinancials,
      [COMPANY_VIEW_SCOPES.HIRING]: showHiringTrends,
      [COMPANY_VIEW_SCOPES.GUIDANCE_COMPANY]: showGuidance,
      [COMPANY_VIEW_SCOPES.SI_HIRING]: hasPartnerHiring,
    },
  };
};

export const isCompanyViewAvailable = (ticker, scope) => (
  Boolean(getCompanyAvailability(ticker).scopes[scope])
);

export const optionSupportsCompanyView = (option, scope) => (
  isCompanyViewAvailable(option?.ticker, scope)
);
